import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import Button from '@material-ui/core/Button';
import Spinner from '@material-ui/core/CircularProgress';

import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { ArchivalUnitCopy, Originator, Params, Result } from 'types';

import { GridRightPanelProps } from './register-types';
import {
  MetadataApiRoute,
  SortOrder,
  ReportTemplateType,
  ApiRoute,
} from 'enums';
import { DialogContext } from 'components/dialog/dialog-context';
import { DownloadMetadaDialog } from 'components/dialog/aisuk-dialogs/download-metadata-dialog';

import { GridRootUrl } from 'components/grid/v2/context/hierarchy-context';

import { useAidsListByOriginator } from 'components/grid/v2/context/aids-context';
import { useArchivalCollections } from 'components/grid/v2/hooks/archival-collection-hook';
import { useQuery } from 'react-query';

import fetch from 'utils/fetch';
import { RegisterPermaLinkDialog } from './register-permalink-dialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: 'calc(100vh - 140px)',
      height: '100%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
    },
    title: {
      padding: theme.spacing(2),
    },
    buttons: {
      padding: 16,
      width: '90%',
    },
    button: {
      marginTop: theme.spacing(),
      alignSelf: 'baseline',
      width: '100%',
    },
  })
);

const useCheckAvailability = (originator?: Originator) => {
  const aidsByOriginator = useAidsListByOriginator(originator?.id);
  const collectiosnByOriginator = useArchivalCollections(originator?.id);

  const auByOriginator = useQuery(
    ['archival-units', originator?.id],
    async () => {
      const params: Params = {
        filters: [
          {
            operation: 'OR',
            filters: [
              {
                operation: 'EQ',
                field: 'originator.id',
                value: originator?.id,
              },
              {
                operation: 'EQ',
                field: 'eadNode.relations.originator.id',
                value: originator?.id,
              },
            ],
          },
        ],
        sort: [{ field: 'numericId', order: SortOrder.ASC }],
        size: 1,
      };
      const res = await fetch(`/api/${ApiRoute.ARCHIVAL_UNIT_LIST}`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });
      return (await res.json()) as Result<ArchivalUnitCopy>;
    },
    { enabled: !!originator }
  );

  if (!originator) {
    return {
      fonds: false,
      aids: false,
      archivalUnits: false,
      loadingAids: aidsByOriginator.isLoading,
      loadingCollections: collectiosnByOriginator.isLoading,
      loadingArchivalUnits: auByOriginator.isLoading,
    };
  }

  const hasAids = Object.entries(aidsByOriginator.data ?? {}).some(
    ([, entry]) => entry.length > 0
  );
  return {
    fonds: (collectiosnByOriginator.data?.count ?? 0) > 0,
    aids: hasAids,
    archivalUnits: (auByOriginator.data?.count ?? 0) > 0,
    loadingAids: aidsByOriginator.isLoading,
    loadingCollections: collectiosnByOriginator.isLoading,
    loadingArchivalUnits: auByOriginator.isLoading,
  };
};

export function GridRightPanel({ detail }: GridRightPanelProps) {
  const classes = useStyles();
  const history = useHistory();
  const { open } = useContext(DialogContext);

  const title = !!detail ? detail.label : 'Zvolte detail';

  const {
    fonds,
    aids,
    archivalUnits,
    loadingAids,
    loadingArchivalUnits,
    loadingCollections,
  } = useCheckAvailability(detail!);

  const handleArchivalCollectionsLink = () => {
    // const params: Params = {
    //   filters: [
    //     {
    //       operation: 'OR',
    //       filters: [
    //         { operation: 'EQ', field: 'originators.id', value: detail?.id },
    //         { operation: 'EQ', field: 'placesOfOrigin.id', value: detail?.id },
    //       ],
    //     },
    //   ],
    // };

    // if (detail) {
    //     setOriginatorFilter?.({ params, originator: detail });
    // }

    history.push(`${GridRootUrl.COLLECTIONS}?originator=${detail?.id}`);
  };

  const handleArchivalAidLink = () => {
    // const params: Params = {
    //   filters: [
    //     {
    //       operation: 'OR',
    //       filters: [
    //         { operation: 'EQ', field: 'authors.id', value: detail?.id },
    //         { operation: 'EQ', field: 'originators.id', value: detail?.id },
    //         { operation: 'EQ', field: 'arrangers.id', value: detail?.id },
    //       ],
    //     },
    //   ],
    // };

    // if (detail) {
    //   setOriginatorFilter?.({ params, originator: detail });
    // }

    history.push(`${GridRootUrl.AIDS}?originator=${detail?.id}`);

    //history.push(`/pomucky`);

    //history.push(`/pomucky${queryParams}`);
  };

  const handleArchivalUnitsLink = () => {
    const params: Params = {
      filters: [
        {
          operation: 'OR',
          filters: [
            {
              operation: 'EQ',
              field: 'originator.id',
              value: detail?.id,
            },
            {
              operation: 'EQ',
              field: 'eadNode.relations.originator.id',
              value: detail?.id,
            },
          ],
        },
      ],
      sort: [{ field: 'numericId', order: SortOrder.ASC }],
    };

    const queryParams = qs.stringify(params, { addQueryPrefix: true });
    history.push(`/archivalie${queryParams}`);
  };

  const handleOpenDialog = () => {
    open({
      initialValues: {
        fileName: `o_${detail?.id}.pdf`,
        data: detail!,
        route: MetadataApiRoute.ORIGINATOR,
        templateType: ReportTemplateType.ORIGINATOR,
      },
      size: 'md',
      Content: (props) => <DownloadMetadaDialog<Originator> {...props} />,
    });
  };

  const handleCreateFixedLink = () => {
    if (detail) {
      open({
        size: 'md',
        Content: (props) => (
          <RegisterPermaLinkDialog {...props} detail={detail} />
        ),
      });
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {title}
      </Typography>

      {!!detail && (
        <div className={classes.buttons}>
          {[
            {
              title: 'Fondy',
              handleClick: handleArchivalCollectionsLink,
              disabled: !fonds,
              loading: loadingCollections,
            },
            {
              title: 'Archivní pomůcky',
              handleClick: handleArchivalAidLink,
              disabled: !aids,
              loading: loadingAids,
            },
            {
              title: 'Archiválie',
              handleClick: handleArchivalUnitsLink,
              disabled: !archivalUnits,
              loading: loadingArchivalUnits,
            },
            {
              title: 'Stáhni popis do PDF/XML',
              handleClick: handleOpenDialog,
            },
            {
              title: 'Vytvořit trvalý odkaz',
              handleClick: handleCreateFixedLink,
            },
          ].map(({ loading, title, handleClick, disabled }) => (
            <Button
              key={title}
              className={classes.button}
              variant="outlined"
              color="primary"
              onClick={handleClick}
              disabled={disabled}
            >
              {title}{' '}
              {loading && (
                <div style={{ marginLeft: 8, transform: 'translateY(2px)' }}>
                  <Spinner size={14} color="inherit" />
                </div>
              )}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
